import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { unwrapFromResult } from '../helpers/resultWrapper';
import { type WrappedTRPCClient } from '../helpers/trpcClientTypes';

// The rest of a post's body (.specs/posts.md → "Content rows"), paged in
// through posts.contentRows and returned assembled so far. Nothing loads on
// scroll, ever: each page is one "View more" click (`loadMore`). `all: true`
// pulls every row up front for the edit form.

type BodyPost = { id: string; content: string; contentRows: number };

// As many rows a call as the server allows: the edit form is waiting.
const ALL_PAGE_SIZE = 50;

export const postBodyQueryKey = (postId: string) => ['postBody', postId];

/** The whole body in one go, for code that cannot wait on a hook. */
export const fetchPostBody = async (
  client: WrappedTRPCClient,
  post: BodyPost
): Promise<string> =>
  post.contentRows > 1
    ? post.content + (await fetchRest(client, post.id))
    : post.content;

const fetchRest = async (
  client: WrappedTRPCClient,
  postId: string
): Promise<string> => {
  let rest = '';
  let cursor = 1;
  for (;;) {
    const page = await unwrapFromResult(
      client.posts.contentRows.query({ postId, cursor, limit: ALL_PAGE_SIZE })
    );
    rest += page.rows.map((row) => row.text).join('');
    if (page.nextCursor === undefined) return rest;
    cursor = page.nextCursor;
  }
};

const usePostBody = (
  client: WrappedTRPCClient,
  post: BodyPost,
  { all = false }: { all?: boolean } = {}
) => {
  const [started, setStarted] = useState(false);
  const hasMore = post.contentRows > 1;

  const pages = useInfiniteQuery({
    queryKey: [...postBodyQueryKey(post.id), 'pages'],
    queryFn: ({ pageParam }) =>
      unwrapFromResult(
        client.posts.contentRows.query({ postId: post.id, cursor: pageParam })
      ),
    initialPageParam: 1,
    getNextPageParam: (last) => last.nextCursor,
    enabled: !all && started && hasMore,
    staleTime: 1000 * 60 * 5
  });

  // Fresh on every open (gcTime 0), fixed while open (staleTime Infinity): the
  // form seeds its state once, and a refetch mid-edit would unmount it.
  const whole = useQuery({
    queryKey: [...postBodyQueryKey(post.id), 'all'],
    queryFn: () => fetchRest(client, post.id),
    enabled: all && hasMore,
    staleTime: Infinity,
    gcTime: 0
  });

  // Gated on hasMore: a disabled query still returns rows cached before an edit.
  const rest = !hasMore
    ? ''
    : all
      ? (whole.data ?? '')
      : (pages.data?.pages
          .flatMap((page) => page.rows.map((row) => row.text))
          .join('') ?? '');
  const complete =
    !hasMore ||
    (all
      ? whole.isSuccess && !whole.isFetching
      : pages.data !== undefined && !pages.hasNextPage);

  return {
    content: post.content + rest,
    // Every row is in `content`.
    complete,
    // One page per call; the first call enables the query.
    loadMore: () => {
      if (!started) return setStarted(true);
      if (pages.isFetching) return;
      if (pages.isError) void pages.refetch();
      else if (pages.hasNextPage) void pages.fetchNextPage();
    },
    isLoading: all ? whole.isLoading : pages.isFetching,
    error: all ? whole.error : pages.error
  };
};

export default usePostBody;
